import { useEffect, useState } from 'react'
import { supabase } from '@/integrations/supabase/client'

export interface EventDetail {
  id: string
  name: string
  description: string
  location: string
  start_date: string | null
  end_date: string | null
  image_url: string | null
  capacity: number | null
  current_participants: number
  ticket_url: string | null
  website_url: string | null
  status: string
  volunteer_categories?: any
  theme?: string
  seeking_volunteers?: boolean
  organized_by_us?: boolean
  category: 'festivals' | 'academies' | 'colivings' | 'workations' | 'retreats'
  topics?: string[]
  primary_cta_link?: string | null
  secondary_cta_link?: string | null
  accommodation_type?: 'none' | 'internal' | 'mangobeds'
  mangobeds_form_id?: string | null
  accommodation_instructions?: string | null
  links?: {
    videos?: Array<{
      url: string;
      title: string;
      platform?: 'youtube' | 'vimeo' | 'direct';
    }>;
  };
}

export const useEventDetail = (category: string, id: string) => {
  const [event, setEvent] = useState<EventDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchEventDetail = async () => {
      try {
        let tableName = ''
        switch (category) {
          case 'festivals':
          case 'nomad-fests':
          case 'snomad-fests':
          case 'ai-fests':
          case 'longevity-fests':
          case 'bootcamps':
          case 'ai-bootcamps':
            tableName = 'festivals'
            break
          case 'academies':
            tableName = 'academies'
            break
          case 'colivings':
            tableName = 'venues'
            break
          case 'workations':
            tableName = 'workations'
            break
          case 'retreats':
            // Using festivals table with specific themes for retreats
            tableName = 'festivals'
            break
          default:
            throw new Error(`Invalid category: ${category}`)
        }

        // Detect if id is a UUID or slug
        const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id);
        const queryField = isUUID ? 'id' : 'slug';

        console.log(`[useEventDetail] Fetching ${category} event with ${queryField}=${id} from table ${tableName}`)

        const { data, error } = category === 'colivings' 
          ? await supabase
              .from(tableName as any)
              .select('*')
              .eq(queryField, id)
              .eq('venue_type', 'coliving')
              .maybeSingle()
          : await supabase
              .from(tableName as any)
              .select('*')
              .eq(queryField, id)
              .maybeSingle()

        let eventRow = data as any | null;

        // Fallbacks if not found by slug (case differences or legacy data)
        if (!eventRow && !isUUID && category !== 'colivings') {
          // Case-insensitive slug match
          const { data: alt1 } = await supabase
            .from(tableName as any)
            .select('*')
            .ilike('slug', id)
            .maybeSingle();
          eventRow = alt1 as any | null;

          // Final fallback: try by name derived from slug
          if (!eventRow) {
            const nameGuess = id.replace(/-/g, ' ');
            const { data: alt2 } = await supabase
              .from(tableName as any)
              .select('*')
              .ilike('name', nameGuess)
              .maybeSingle();
            eventRow = alt2 as any | null;
          }
        }

        if (error) {
          console.error(`[useEventDetail] Database error:`, error)
          if (error.code === 'PGRST116') {
            throw new Error(`Event not found or you don't have permission to view it`)
          }
          throw new Error(`Database error: ${error.message}`)
        }

        if (eventRow) {
          console.log(`[useEventDetail] Event found:`, eventRow)
          const eventData = eventRow as any
          setEvent({
            id: eventData.id,
            name: eventData.name || eventData.title,
            description: eventData.description || '',
            location: eventData.location || '',
            start_date: eventData.start_date,
            end_date: eventData.end_date,
            image_url: eventData.image_url,
            capacity: eventData.capacity || eventData.max_participants,
            current_participants: eventData.current_participants || 0,
            ticket_url: eventData.ticket_url,
            website_url: eventData.website_url || eventData.website,
            status: eventData.status || 'upcoming',
            volunteer_categories: eventData.volunteer_categories,
            theme: eventData.theme,
            seeking_volunteers: eventData.seeking_volunteers,
            organized_by_us: eventData.organized_by_us,
            category: category as EventDetail['category'],
            topics: eventData.topics || [],
            primary_cta_link: eventData.primary_cta_link,
            secondary_cta_link: eventData.secondary_cta_link,
            accommodation_type: eventData.accommodation_type || 'none',
            mangobeds_form_id: eventData.mangobeds_form_id,
            accommodation_instructions: eventData.accommodation_instructions,
            links: eventData.links || {}
          })
        } else {
          console.log(`[useEventDetail] No data returned for ${category}/${id}`)
          setError(`Event not found`)
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (id && category) {
      fetchEventDetail()
    }
  }, [id, category])

  const refetch = () => {
    if (id && category) {
      setLoading(true)
      fetchEventDetail()
    }
  }

  return { event, loading, error, refetch }
}